import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import "../styles/login/login.css";
import { useTranslation } from 'react-i18next';
import Header from "./Header";
import Footer from "./Footer";
import { useAuth } from "./context/AuthContext";
import { Roles } from "../roles";
export const getUserData = async (response) => {
  if (!response.ok) throw new Error("Network response was not ok");

  const data = await response.json();

  if (!data.token || !data.userId || !data.userName) {
    throw new Error("Incomplete user data");
  }

  return data;
};

// Helper function to verify OTP API call
export const verifyOtp = async (email, otp, apiUrl) => {
  const response = await fetch(`${apiUrl}/verify-otp`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ email, otp }),
  });

  return getUserData(response);
};

export default function Otp() {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { login } = useAuth();
  const { lang } = useParams();
  const email = location.state?.email;
  const [otp, setOtp] = useState("");
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [secondsLeft, setSecondsLeft] = useState(30);
  const [loading, setLoading] = useState(false);
  const apiUrl = process.env.REACT_APP_OPT_SERVER;

  const clearErrorAfterDelay = useCallback(() => {
    const timer = setTimeout(() => {
      setError("");
      setMessage("");
    }, 5000);
    return () => clearTimeout(timer);
  }, []);

  // Countdown timer for resend
  useEffect(() => {
    if (secondsLeft > 0) {
      const timer = setTimeout(() => {
        setSecondsLeft((prev) => prev - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [secondsLeft]);

  // Validate email on load
  useEffect(() => {
    const isValidEmail = () => {
      if (!email) return false;
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return emailRegex.test(email);
    };

    if (!isValidEmail()) {
      navigate(`/lang/${lang}/login`, { replace: true });
    }
  }, [email, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!otp) {
      setError(t('err-otp-empty'));
      clearErrorAfterDelay();
      return;
    }

    setError("");
    setMessage("");
    setLoading(true);

    try {
      // Use the helper verifyOtp function here
      const data = await verifyOtp(email, otp, apiUrl);
      const { token, role, ...safeUserData } = data

      login(safeUserData, token, role);

      setMessage(t('verify-success'));
      clearErrorAfterDelay();
      // setUserData(data); // Optional: if you still need it

      const username = safeUserData.userName;

      if (role === Roles.USER) {
        navigate(`/lang/${lang}/files/${username}`, { state: { data } });
        // } else if (role === Roles.ADMIN || role === Roles.SUPERADMIN) {
      } else if (role === Roles.ADMIN || role === Roles.SUPERADMIN) {
        navigate(`/lang/${lang}/admin/dashboard`, { state: { data } });
      } else {
        navigate(`/lang/${lang}/unauthorized`);
      }


    } catch (err) {
      setError(t('verify-fail'));
      clearErrorAfterDelay();
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = async () => {
    try {
      const response = await fetch(`${apiUrl}/generate-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      // console.log("api", apiUrl);
      if (!response.ok) {
        throw new Error(t('more-err'));
      }

      const data = await response.text();
      setMessage(data.message || t('resend-sucess'));
      setSecondsLeft(30);
      clearErrorAfterDelay();
    } catch (error) {
      setError(error.message || t('resend-fail'));
      clearErrorAfterDelay();
    }
  };

  return (
    <>
      <Header hideNav={true} hideHamburger={true} />
      <div className="login-container">
        <div className="login-form">
          <form onSubmit={handleSubmit}>

            {message && (
              <div
                className="alert-box success"
                role="alert"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="20px" height="20px" viewBox="0 0 24 24"><path fill="currentColor" fill-rule="evenodd" d="M12 21a9 9 0 0 0 7.51-13.961l-7.155 7.95a2 2 0 0 1-2.687.262L6.4 12.8a1 1 0 0 1 1.2-1.6l3.268 2.451l7.346-8.161A9 9 0 1 0 12 21" clip-rule="evenodd" /></svg>
                <span>{message}</span>
                <button onClick={() => setMessage("")}>&times;</button>
              </div>
            )}

            {error && (
              <div
                className="alert-box error"
                role="alert"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="20px" height="20px" viewBox="0 0 24 24"><path fill="currentColor" d="M12 17q.425 0 .713-.288T13 16t-.288-.712T12 15t-.712.288T11 16t.288.713T12 17m0-4q.425 0 .713-.288T13 12V8q0-.425-.288-.712T12 7t-.712.288T11 8v4q0 .425.288.713T12 13m0 9q-2.075 0-3.9-.788t-3.175-2.137T2.788 15.9T2 12t.788-3.9t2.137-3.175T8.1 2.788T12 2t3.9.788t3.175 2.137T21.213 8.1T22 12t-.788 3.9t-2.137 3.175t-3.175 2.138T12 22" /></svg>
                <span>{error}</span>
                <button onClick={() => setError("")}>&times;</button>
              </div>
            )}
            {/* <div className="login-logo">
              <img src="/assets/MPTC_Logo.png" alt="Logo" className="logo-image" />
            </div> */}

            <label className="login-label">
              {t('otp-label')}
              <strong>{email ? email : "e.g. bopha@gov.kh"}</strong> <span className="required">*</span>
            </label>

            <input
              type="text"
              placeholder="e.g ABCDEF"
              value={otp}
              onChange={(e) => {
                setOtp(e.target.value);
                if (error) setError("");
              }}
              className={`login-input ${error ? "error" : ""}`}
              disabled={loading}
            />

            {/* {error && <p className="error">{error}</p>} */}
            <button type="submit" className="login-button" disabled={loading} >
              {loading ? t('otp-loading') : t('otp-button')}
            </button>
            {/* {message && <p className="message">{message}</p>} */}
            <div className="otp-message">
              <span>{t('otp-message')}{" "}</span>
              {secondsLeft > 0 ? (
                <span className="otp-countdown">
                  {t('otp-countdown')} {secondsLeft}s
                </span>
              ) : (
                <button
                  disabled={secondsLeft > 0}
                  type="button"
                  onClick={handleResendOTP}
                  className="otp-resend-link"
                >
                  {secondsLeft > 0
                    ? `${t('otp-countdown')} ${secondsLeft}s`
                    : t('otp-resend')
                  }
                </button>
              )}
            </div>
          </form>

          <img src="/assets/Desktop_background.svg" alt="banner" className="desktop_banner" />
          <img src="/assets/Mobile_background.svg" alt="banner" className="mobile_banner" />
        </div >
      </div >
      <Footer hideFooter={true} />
    </>
  );
}
